// src/components/TopDonorsAdvanced.js
/**
 * TopDonorsAdvanced (v5) — full file (minor change: iOS shows Latin "Om Namah Shivaya")
 *
 * NOTE: for best results on iOS, keep the Google Fonts <link> changes you already added.
 */

import React, { useEffect, useState, useRef, useCallback } from "react";
import { fetchAboveFifty } from "../api/api";
import {
  Box,
  Text,
  Flex,
  Spinner,
  useBreakpointValue,
  VStack,
  Center,
  VisuallyHidden,
  IconButton,
  HStack,
} from "@chakra-ui/react";
import { motion, AnimatePresence } from "framer-motion";
import { ChevronLeftIcon, ChevronRightIcon } from "@chakra-ui/icons";

const MotionBox = motion(Box);

////////////////////////////////////////////////////////////////////////////////
// OmSVG — improved iOS handling: show Latin transliteration on iOS only
////////////////////////////////////////////////////////////////////////////////

const OmSVG = ({ size = "100%", strong = false, isiOS = false }) => {
  // On iOS, replace Telugu phrase with Latin transliteration per your request.
  // On other platforms keep Telugu (NFC normalized).
  const teluguOriginal = "ఓం నమః శివాయ";
  const latinTranslit = "Om Namah Shivaya";
  const topLine = isiOS ? latinTranslit : teluguOriginal.normalize("NFC");
  const bottomLine = isiOS ? latinTranslit : teluguOriginal.normalize("NFC");

  const circleOpacity = strong ? 0.66 : 0.55;
  const mainOmOpacity = strong ? 0.94 : 0.88;
  const teluguOpacity = strong ? 0.85 : 0.78;
  const phraseOpacity = strong ? 0.8 : 0.72;

  const goldStops = strong
    ? [
        { off: "0%", color: "#ffe596" },
        { off: "60%", color: "#f3b94a" },
        { off: "100%", color: "#c98f2a" },
      ]
    : [
        { off: "0%", color: "#ffd27a" },
        { off: "60%", color: "#f7c25a" },
        { off: "100%", color: "#d9a64a" },
      ];

  // Preferred font stacks for iOS; these are only used when isiOS === true.
  const devanagariFontIOS =
    '"Noto Serif Devanagari", "Devanagari Sangam MN", "Mukta Vaani", serif';
  const teluguFontStackIOS =
    '"Noto Sans Telugu", "Telugu Sangam MN", "Kohinoor Telugu", system-ui, serif';
  // for latin transliteration, prefer a clean system font on iOS
  const latinFontIOS = '"Helvetica Neue", "Arial", system-ui, sans-serif';

  const devanagariFont = isiOS ? devanagariFontIOS : "serif";
  // If isiOS and we're showing Latin, prefer latinFontIOS; otherwise telugu stack (or serif fallback).
  const teluguFontFamily = isiOS ? latinFontIOS : teluguFontStackIOS;

  return (
    <svg
      viewBox="0 0 720 360"
      preserveAspectRatio="xMidYMid meet"
      width={size}
      height={size}
      aria-hidden
      role="img"
      lang={isiOS ? "en" : "te"}
    >
      <defs>
        <linearGradient id="omGoldGrad" x1="0" x2="1" y1="0" y2="1">
          <stop offset={goldStops[0].off} stopColor={goldStops[0].color} />
          <stop offset={goldStops[1].off} stopColor={goldStops[1].color} />
          <stop offset={goldStops[2].off} stopColor={goldStops[2].color} />
        </linearGradient>

        <radialGradient id="omVignette" cx="50%" cy="50%" r="60%">
          <stop
            offset="0%"
            stopColor="#fff8ee"
            stopOpacity={strong ? 0.44 : 0.36}
          />
          <stop offset="100%" stopColor="#fff8ee" stopOpacity="0" />
        </radialGradient>

        <filter id="omBlur" x="-30%" y="-30%" width="160%" height="160%">
          <feGaussianBlur stdDeviation={strong ? 2.6 : 3} result="b" />
          <feMerge>
            <feMergeNode in="b" />
            <feMergeNode in="SourceGraphic" />
          </feMerge>
        </filter>

        <filter id="omSoftGlow" x="-40%" y="-40%" width="180%" height="180%">
          <feGaussianBlur stdDeviation={strong ? 6.6 : 8} result="g" />
          <feMerge>
            <feMergeNode in="g" />
            <feMergeNode in="SourceGraphic" />
          </feMerge>
        </filter>

        {/* CSS rules inside SVG to encourage correct shaping/smoothing */}
        <style type="text/css">{`
          .om-devanagari { 
            font-feature-settings: "liga" 1, "clig" 1; 
            -webkit-font-smoothing: antialiased; 
            text-rendering: optimizeLegibility; 
            paint-order: stroke fill; 
          }
          .om-telugu, .om-latin {
            font-feature-settings: "liga" 1, "clig" 1; 
            -webkit-font-smoothing: antialiased; 
            text-rendering: geometricPrecision;
          }
        `}</style>
      </defs>

      {/* very soft circular background glow */}
      <circle
        cx="360"
        cy="180"
        r="160"
        fill="url(#omVignette)"
        opacity={circleOpacity}
      />

      {/* Large ॐ glyph (center) — unchanged */}
      <text
        x="50%"
        y="40%"
        textAnchor="middle"
        dominantBaseline="middle"
        fontSize="160"
        fontFamily={devanagariFont}
        className="om-devanagari"
        fill="url(#omGoldGrad)"
        style={{
          transform: "translateY(-6%)",
          WebkitFontSmoothing: "antialiased",
          textRendering: "geometricPrecision",
        }}
        filter="url(#omSoftGlow)"
        opacity={mainOmOpacity}
      >
        ॐ
      </text>

      {/* Middle line — Telugu or Latin depending on isiOS */}
      <text
        x="50%"
        y="62%"
        textAnchor="middle"
        dominantBaseline="middle"
        fontSize={isiOS ? "36" : "46"}
        fontFamily={teluguFontFamily}
        className={isiOS ? "om-latin" : "om-telugu"}
        fill="#d9a64a"
        style={{
          letterSpacing: isiOS ? "0.06em" : "0.02em",
          WebkitFontSmoothing: "antialiased",
          textRendering: "geometricPrecision",
          shapeRendering: "geometricPrecision",
        }}
        filter="url(#omBlur)"
        opacity={teluguOpacity}
      >
        {topLine}
      </text>

      {/* Bottom line — phrase or transliteration */}
      <text
        x="50%"
        y="76%"
        textAnchor="middle"
        dominantBaseline="middle"
        fontSize={isiOS ? "24" : "30"}
        fontFamily={teluguFontFamily}
        className={isiOS ? "om-latin" : "om-telugu"}
        fill="#b9852a"
        style={{
          letterSpacing: isiOS ? "0.04em" : "0.01em",
          WebkitFontSmoothing: "antialiased",
          textRendering: "geometricPrecision",
        }}
        opacity={phraseOpacity}
      >
        {bottomLine}
      </text>
    </svg>
  );
};

////////////////////////////////////////////////////////////////////////////////
// Ornate border (unchanged)
////////////////////////////////////////////////////////////////////////////////

function OrnateBorder() {
  return (
    <svg
      viewBox="0 0 600 360"
      preserveAspectRatio="none"
      width="100%"
      height="100%"
      style={{ display: "block", pointerEvents: "none" }}
      aria-hidden
    >
      <defs>
        <linearGradient id="goldGrad" x1="0" x2="1" y1="0" y2="1">
          <stop offset="0%" stopColor="#ffd27a" />
          <stop offset="40%" stopColor="#ffcf63" />
          <stop offset="100%" stopColor="#e6b23a" />
        </linearGradient>

        <radialGradient id="glow" cx="50%" cy="50%" r="60%">
          <stop offset="0%" stopColor="#fff7e6" stopOpacity="0.45" />
          <stop offset="100%" stopColor="#fff7e6" stopOpacity="0" />
        </radialGradient>

        <pattern
          id="tinyPattern"
          width="8"
          height="8"
          patternUnits="userSpaceOnUse"
        >
          <rect width="8" height="8" fill="rgba(255,255,255,0.02)" />
          <path
            d="M0 4 L4 4"
            stroke="rgba(255,255,255,0.03)"
            strokeWidth="0.5"
          />
        </pattern>

        <filter id="softShadow" x="-50%" y="-50%" width="200%" height="200%">
          <feDropShadow
            dx="0"
            dy="6"
            stdDeviation="10"
            floodColor="#000"
            floodOpacity="0.12"
          />
        </filter>
      </defs>

      {/* outer glow */}
      <rect
        x="6"
        y="6"
        rx="20"
        ry="20"
        width="588"
        height="348"
        fill="url(#glow)"
        opacity="0.35"
      />

      {/* patterned inner fill */}
      <rect
        x="12"
        y="12"
        rx="16"
        ry="16"
        width="576"
        height="336"
        fill="url(#tinyPattern)"
        opacity="0.6"
      />

      {/* main ornate stroke */}
      <rect
        x="14"
        y="14"
        rx="16"
        ry="16"
        width="572"
        height="332"
        fill="none"
        stroke="url(#goldGrad)"
        strokeWidth="4"
        strokeLinejoin="round"
        filter="url(#softShadow)"
      />

      {/* inner highlight stroke */}
      <rect
        x="20"
        y="20"
        rx="12"
        ry="12"
        width="560"
        height="320"
        fill="none"
        stroke="rgba(255,255,255,0.45)"
        strokeWidth="1.2"
      />

      {/* corner filigree — four corners */}
      <g
        transform="translate(36,36)"
        fill="none"
        stroke="url(#goldGrad)"
        strokeWidth="1.6"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <path d="M0 18 C12 2, 38 -2, 56 8 C74 18, 78 44, 60 60" />
        <path d="M8 24 C18 12, 34 8, 48 14" strokeOpacity="0.9" />
        <circle cx="0" cy="0" r="2.6" fill="#e6b23a" stroke="none" />
      </g>

      <g
        transform="translate(564,36) scale(-1,1)"
        fill="none"
        stroke="url(#goldGrad)"
        strokeWidth="1.6"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <path d="M0 18 C12 2, 38 -2, 56 8 C74 18, 78 44, 60 60" />
        <path d="M8 24 C18 12, 34 8, 48 14" strokeOpacity="0.9" />
        <circle cx="0" cy="0" r="2.6" fill="#e6b23a" stroke="none" />
      </g>

      <g
        transform="translate(36,324) scale(1,-1)"
        fill="none"
        stroke="url(#goldGrad)"
        strokeWidth="1.6"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <path d="M0 18 C12 2, 38 -2, 56 8 C74 18, 78 44, 60 60" />
        <path d="M8 24 C18 12, 34 8, 48 14" strokeOpacity="0.9" />
        <circle cx="0" cy="0" r="2.6" fill="#e6b23a" stroke="none" />
      </g>

      <g
        transform="translate(564,324) scale(-1,-1)"
        fill="none"
        stroke="url(#goldGrad)"
        strokeWidth="1.6"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <path d="M0 18 C12 2, 38 -2, 56 8 C74 18, 78 44, 60 60" />
        <path d="M8 24 C18 12, 34 8, 48 14" strokeOpacity="0.9" />
        <circle cx="0" cy="0" r="2.6" fill="#e6b23a" stroke="none" />
      </g>

      {/* small 'tilak' accent at top-center */}
      <g transform="translate(300,18)">
        <path
          d="M0 0 C6 -10, 18 -10, 24 0 C18 -2, 6 -2, 0 0"
          fill="url(#goldGrad)"
          opacity="0.98"
        />
      </g>
    </svg>
  );
}

////////////////////////////////////////////////////////////////////////////////
// Helpers + main component (kept unchanged except OmSVG usage)
////////////////////////////////////////////////////////////////////////////////

const formatINR = (value) =>
  value?.toLocaleString("en-IN", { maximumFractionDigits: 0 });

export default function TopDonorsAdvanced({ interval = 4200 }) {
  const [donors, setDonors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [idx, setIdx] = useState(0);
  const [error, setError] = useState(null);

  const isMobile = useBreakpointValue({ base: true, md: false });
  const cardPadding = useBreakpointValue({ base: 4, md: 6 });
  const plateWidth = useBreakpointValue({ base: "96%", md: "560px" });

  const timerRef = useRef(null);
  const containerRef = useRef(null);
  const pausedRef = useRef(false);

  // detect iOS conservatively (only in browser)
  const isIOS =
    typeof navigator !== "undefined" &&
    (/iPhone|iPad|iPod/i.test(navigator.userAgent) ||
      (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1));

  // touch detection
  const touchStartX = useRef(0);
  const touchStartY = useRef(0);

  // helper: Fisher–Yates shuffle
  const shuffleArray = (arr) => {
    const a = arr.slice();
    for (let i = a.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [a[i], a[j]] = [a[j], a[i]];
    }
    return a;
  };

  // fetch donors — SHUFFLE RANDOMLY
  useEffect(() => {
    let mounted = true;
    setLoading(true);
    setError(null);

    fetchAboveFifty()
      .then((res) => {
        const data = res?.data ?? [];
        const shuffled = Array.isArray(data) ? shuffleArray(data) : [];
        if (mounted) setDonors(shuffled);
      })
      .catch((err) => {
        console.error("TopDonorsAdvanced fetch error:", err);
        if (mounted) setError("Unable to load donors");
      })
      .finally(() => mounted && setLoading(false));

    return () => {
      mounted = false;
    };
  }, []);

  // start auto-cycle
  const startTimer = useCallback(() => {
    if (timerRef.current) clearInterval(timerRef.current);
    if (donors.length <= 1) return;
    timerRef.current = setInterval(
      () => setIdx((s) => (s + 1) % donors.length),
      interval
    );
  }, [donors.length, interval]);

  useEffect(() => {
    if (!pausedRef.current) startTimer();
    return () => clearInterval(timerRef.current);
  }, [donors.length, startTimer]);

  // prev / next controls
  const goPrev = useCallback(
    () => setIdx((s) => (s - 1 + donors.length) % donors.length),
    [donors.length]
  );
  const goNext = useCallback(
    () => setIdx((s) => (s + 1) % donors.length),
    [donors.length]
  );

  // keyboard nav
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "ArrowLeft") goPrev();
      if (e.key === "ArrowRight") goNext();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [goPrev, goNext]);

  // pause on hover / resume
  useEffect(() => {
    const node = containerRef.current;
    if (!node) return;
    const onEnter = () => {
      pausedRef.current = true;
      if (timerRef.current) clearInterval(timerRef.current);
    };
    const onLeave = () => {
      pausedRef.current = false;
      startTimer();
    };
    node.addEventListener("mouseenter", onEnter);
    node.addEventListener("mouseleave", onLeave);
    node.addEventListener("touchstart", onEnter, { passive: true });
    node.addEventListener("touchend", onLeave, { passive: true });

    return () => {
      node.removeEventListener("mouseenter", onEnter);
      node.removeEventListener("mouseleave", onLeave);
      node.removeEventListener("touchstart", onEnter);
      node.removeEventListener("touchend", onLeave);
    };
  }, [startTimer]);

  // touch handlers (non-blocking vertical scroll)
  const handleTouchStart = (e) => {
    const t = e.touches?.[0];
    if (!t) return;
    touchStartX.current = t.clientX;
    touchStartY.current = t.clientY;
  };
  const handleTouchEnd = (e) => {
    const t = e.changedTouches?.[0];
    if (!t) return;
    const dx = t.clientX - touchStartX.current;
    const dy = t.clientY - touchStartY.current;
    if (Math.abs(dx) > 50 && Math.abs(dx) > Math.abs(dy)) {
      if (dx > 0) goPrev();
      else goNext();
      // restart timer for clarity
      if (timerRef.current) {
        clearInterval(timerRef.current);
        startTimer();
      }
    }
  };

  if (loading)
    return (
      <Center py={6}>
        <Spinner color="orange.400" thickness="4px" size="lg" />
      </Center>
    );

  if (error)
    return (
      <Center py={6}>
        <Text color="red.500">{error}</Text>
      </Center>
    );

  if (!donors.length)
    return (
      <Center py={6}>
        <Text>No donors above threshold yet</Text>
      </Center>
    );

  const donor = donors[idx % donors.length];
  const donorName = `${donor.donorLastName || ""} ${
    donor.donorFirstName || ""
  }`.trim();

  // animations
  const containerVariants = {
    initial: { opacity: 0, scale: 0.992, y: 8 },
    animate: {
      opacity: 1,
      scale: 1,
      y: 0,
      transition: { duration: 0.6, ease: [0.22, 1, 0.36, 1] },
    },
    exit: {
      opacity: 0,
      scale: 0.99,
      y: -8,
      transition: { duration: 0.45, ease: "easeInOut" },
    },
  };
  const nameVariants = {
    initial: { opacity: 0, y: 8 },
    animate: { opacity: 1, y: 0, transition: { duration: 0.45, delay: 0.06 } },
    exit: { opacity: 0, y: -6, transition: { duration: 0.35 } },
  };
  const amountVariants = {
    initial: { opacity: 0, x: 18 },
    animate: { opacity: 1, x: 0, transition: { duration: 0.45, delay: 0.12 } },
    exit: { opacity: 0, x: 12, transition: { duration: 0.3 } },
  };

  const plateStyle = {
    background:
      "linear-gradient(135deg, #fff8ea 0%, #fff2d8 45%, #ffe8b4 100%)",
    border: "1px solid rgba(0,0,0,0.06)",
    boxShadow:
      "0 14px 40px rgba(11,11,11,0.10), inset 0 2px 0 rgba(255,255,255,0.58)",
    borderRadius: "20px",
    position: "relative",
    overflow: "hidden",
  };

  const embossedStyle = {
    textShadow: "0 1px 0 rgba(255,255,255,0.6), 0 1px 2px rgba(0,0,0,0.06)",
    color: "#2b1500",
    fontWeight: 700,
    letterSpacing: "0.02em",
  };

  return (
    <Center py={6}>
      <Box
        ref={containerRef}
        width={plateWidth}
        sx={{ WebkitTextSizeAdjust: "100%" }}
        px={4}
        onTouchStart={handleTouchStart}
        onTouchEnd={handleTouchEnd}
      >
        <MotionBox
          {...plateStyle}
          p={cardPadding}
          initial="initial"
          animate="animate"
          exit="exit"
          variants={containerVariants}
        >
          {/* watermark */}
          <MotionBox
            position="absolute"
            inset={0}
            display="flex"
            alignItems="center"
            justifyContent="center"
            pointerEvents="none"
            aria-hidden
            zIndex={1}
            initial={{ scale: 1 }}
            animate={{ scale: 1.01 }}
            transition={{ duration: 6, yoyo: Infinity, ease: "linear" }}
            style={{ opacity: 0.26 }}
          >
            {/* pass isiOS so Telugu normalization + font stack apply only on iOS */}
            <OmSVG size="82%" strong isiOS={isIOS} />
          </MotionBox>

          {/* ornate border overlay */}
          <Box position="absolute" inset={0} zIndex={5} pointerEvents="none">
            <OrnateBorder />
          </Box>

          {/* content area */}
          <Box
            position="relative"
            zIndex={6}
            minH={{ base: "96px", md: "120px" }}
          >
            <AnimatePresence mode="wait">
              <MotionBox
                key={donor.id}
                initial="initial"
                animate="animate"
                exit="exit"
                variants={containerVariants}
                aria-live="polite"
              >
                <Flex
                  align="center"
                  justify="space-between"
                  gap={3}
                  minWidth={0}
                >
                  <Box flex="1 1 auto" minW={0} overflow="hidden" pr={3}>
                    <VStack align="start" spacing={1}>
                      <MotionBox variants={nameVariants}>
                        <Text
                          as="div"
                          fontSize={{ base: "14px", md: "16px" }}
                          lineHeight="1.06"
                          sx={embossedStyle}
                          noOfLines={isMobile ? 2 : 1}
                          whiteSpace="normal"
                          overflow="hidden"
                          textOverflow="ellipsis"
                          minW={0}
                        >
                          {donorName}
                        </Text>
                      </MotionBox>

                      <MotionBox variants={nameVariants}>
                        <Text
                          as="div"
                          fontSize={{ base: "11px", md: "13px" }}
                          opacity={0.9}
                          color="rgba(0,0,0,0.55)"
                          noOfLines={1}
                          whiteSpace="nowrap"
                          overflow="hidden"
                          textOverflow="ellipsis"
                          minW={0}
                        >
                          {new Date(donor.timestamp).toLocaleDateString()}
                        </Text>
                      </MotionBox>
                    </VStack>
                  </Box>

                  <MotionBox variants={amountVariants} flex="0 0 auto">
                    <Box
                      bg="linear-gradient(180deg,#fff7ea,#ffe9bb)"
                      px={{ base: 3, md: 4 }}
                      py={{ base: 2, md: 2 }}
                      borderRadius="14px"
                      border="1px solid rgba(0,0,0,0.06)"
                      boxShadow="0 8px 26px rgba(0,0,0,0.08)"
                      minW="110px"
                      textAlign="right"
                    >
                      <Text
                        fontSize={{ base: "12px", md: "14px" }}
                        color="#7a3c00"
                        fontWeight="700"
                      >
                        ₹ {formatINR(donor.amount)}
                      </Text>
                      <Text
                        fontSize={{ base: "10px", md: "11px" }}
                        color="#9a6b3a"
                        opacity={0.95}
                      >
                        Donation
                      </Text>
                    </Box>
                  </MotionBox>
                </Flex>
              </MotionBox>
            </AnimatePresence>
          </Box>

          {/* caption */}
          <Box mt={4}>
            <Box
              borderRadius="full"
              bg="linear-gradient(90deg, rgba(255,215,0,0.95), rgba(255,183,0,0.9))"
              px={{ base: 3, md: 4 }}
              py={{ base: 1.5, md: 2 }}
              boxShadow="0 6px 18px rgba(0,0,0,0.1)"
              textAlign="center"
            >
              <Text
                fontSize={{ base: "11px", md: "12px" }}
                fontWeight="600"
                color="#5b3300"
              >
                Honouring devotees who contributed{" "}
                <Box as="span" fontWeight="extrabold">
                  ₹ 50,000
                </Box>{" "}
                and above
              </Text>
            </Box>
          </Box>

          {/* controls */}
          <Box
            position="absolute"
            left="8px"
            top="50%"
            transform="translateY(-50%)"
            zIndex={7}
            display={{ base: "none", md: "block" }}
          >
            <IconButton
              aria-label="Previous donor"
              icon={<ChevronLeftIcon />}
              size="sm"
              onClick={goPrev}
            />
          </Box>
          <Box
            position="absolute"
            right="8px"
            top="50%"
            transform="translateY(-50%)"
            zIndex={7}
            display={{ base: "none", md: "block" }}
          >
            <IconButton
              aria-label="Next donor"
              icon={<ChevronRightIcon />}
              size="sm"
              onClick={goNext}
            />
          </Box>

          {/* dots */}
          <HStack
            spacing={2}
            justify="center"
            mt={3}
            position="relative"
            zIndex={7}
          >
            {donors.map((_, i) => (
              <Box
                key={i}
                as="button"
                onClick={() => setIdx(i)}
                aria-label={`Show donor ${i + 1}`}
                width={i === idx ? "10px" : "8px"}
                height={i === idx ? "10px" : "8px"}
                borderRadius="full"
                bg={i === idx ? "orange.400" : "rgba(0,0,0,0.12)"}
              />
            ))}
          </HStack>
        </MotionBox>

        <VisuallyHidden aria-live="polite">{`Displaying donor ${donorName} amount ${formatINR(
          donor.amount
        )}`}</VisuallyHidden>
      </Box>
    </Center>
  );
}
